import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { SyncStatus } from '@boma/supplier-portal/shared/entities';
import { catchError, map, Observable, throwError } from 'rxjs';

export class ZraProductDetail {
  id: string;
  /**
   * This is the product id from the catalogue
   */
  productId: string;
  /**
   * This is the classification code from the ZRA API
   */
  classificationCode: string;
  /**
   * This is the external item id from the ZRA API
   */
  externalItemId: string;
  classificationName: string;
  /**
   * The tax code for this product
   */
  taxCode?: string;
  /**
   * The tax code type:
   * "1" = Raw Material
   * "2" = Product
   * "3" = Service
   */
  taxCodeType?: string;
  createdAt: string;
  updatedAt: string;
  latestSyncJob?: ProductZraDetailsSyncJob;

  constructor(init?: Partial<ZraProductDetail>) {
    Object.assign(this, init);
  }
}

export class ProductZraDetailsSyncJob {
  id: string;
  processedAt?: string;
  createdAt: string;
  updatedAt: string;

  constructor(init?: Partial<ProductZraDetailsSyncJob>) {
    Object.assign(this, init);
  }
}


export interface CreateOrUpdateRequest {
  productId: string;
  classificationCode: string;
  externalItemId?: string;
  syncStatus?: SyncStatus;
  lastSyncDate?: string;
  taxCode?: string;
  taxCodeType?: string;
}

export interface ZraApiError {
  statusCode: number;
  message: string;
  details?: string;
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class ZraProductDetailService {
  private base: string;
  private version: string;
  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private http: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  getProductDetails(
    entityId: string,
    productId: string
  ): Observable<ZraProductDetail> {
    return this.http
      .get<ZraProductDetail>(
        `${this.base}api/v${this.version}/${entityId}/ProductZraDetails/${productId}`
      )
      .pipe(
        map((response) => new ZraProductDetail(response)),
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, 'Failed to get product details')
        )
      );
  }

  createOrUpdate(
    entityId: string,
    request: CreateOrUpdateRequest
  ): Observable<ZraProductDetail> {
    return this.http
      .post<ZraProductDetail>(
        `${this.base}api/v${this.version}/${entityId}/ProductZraDetails`,
        request
      )
      .pipe(
        map((response) => new ZraProductDetail(response)),
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, 'Failed to create or update product details')
        )
      );
  }

  syncWithZra(
    entityId: string,
    productId: string
  ): Observable<SyncWithZraResponse> {
    return this.http
      .post<SyncWithZraResponse>(
        `${this.base}api/v${this.version}/${entityId}/ProductZraDetails/${productId}/Sync`,
        {}
      )
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, 'Failed to sync with ZRA')
        )
      );
  }

  private handleError(
    error: HttpErrorResponse,
    defaultMessage: string
  ): Observable<never> {
    let errorMessage = defaultMessage;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Server-side error
      try {
        const apiError = error.error as ZraApiError;
        errorMessage =
          apiError.message ||
          `Server error: ${error.status} - ${error.statusText}`;
        if (apiError.details) {
          errorMessage += ` (${apiError.details})`;
        }
      } catch {
        errorMessage = `Server error: ${error.status} - ${
          error.statusText || defaultMessage
        }`;
      }
    }

    return throwError(() => ({
      message: errorMessage,
      status: error.status,
      originalError: error,
    }));
  }
}

export interface SyncWithZraResponse {
  productId: string;
  externalItemId: string;
  syncStatus: SyncStatus;
  lastSyncDate: string;
}
